/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, TextField, Typography, Box, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { Dispatch, SetStateAction, useEffect, useState, } from "react";
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { NumericFormatDecimalLocale, NumericFormatWhole } from '../../../common/CustomFunctions';
import { DatePicker } from '@mui/x-date-pickers';
import { IApproverDocumentGuiModel } from '../../../model/IApproverGuiModel';
import AccountBusinessLogic from '../../../businessLogic/AccountBusinessLogic';
import { IAccountGuiModel } from '../../../model/IAccountGuiModel';

interface IInvoiceInformation {
    organizationId: string | undefined,
    document: IApproverDocumentGuiModel | undefined,
    setDocument: Dispatch<SetStateAction<IApproverDocumentGuiModel | undefined>>,
    onlyReasign?: boolean
}

const DialogDocumentApproverInvoiceInformation = (props: IInvoiceInformation): JSX.Element => {

    const theme = useTheme();
    const { t, i18n } = useTranslation();

    const [loader, setLoader] = useState(false);
    const [supplierList, setSupplierList] = useState<IAccountGuiModel[]>([]);

    function amountError() {
        if (props.document?.amount && props.document.amount !== "" && /^\d+(,\d+)?$/.test(props.document.amount) === false) {
            return true;
        } else {
            return false;
        }
    }
    function cidError() {
        if (props.document?.cid && props.document.cid !== "" && /^[\d]{1,24}[\d\-]$/.test(props.document.cid) === false) {
            return true;
        } else {
            return false;
        }
    }

    useEffect(() => {

        let isMounted = true; //Avoid memory leaks. Async function running after dismount.

        getSuppliers(isMounted);

        return () => { isMounted = false };

    }, [props.document, props.organizationId])

    function handleDocumentUpdate(
        variant: "supplierId" | "type" | "invoiceNo" | "voucherDate" | "dueDate" | "amount" | "cid",
        value: string | undefined,
        valueDate: Date | undefined | null,
        valueType: "Invoice" | "CreditNote" | "Unknown " = "Invoice",
    ) {

        if (props.document) {
            let updatedDocument: IApproverDocumentGuiModel = {
                id: props.document.id,
                amount: variant === "amount" ? value : props.document.amount,
                invoiceNo: variant === "invoiceNo" ? value : props.document.invoiceNo,
                voucherDate: variant === "voucherDate" ? valueDate : props.document.voucherDate,
                dueDate: variant === "dueDate" ? valueDate : props.document.dueDate,
                cid: variant === "cid" ? value : props.document.cid,
                supplierId: variant === "supplierId" ? value ? parseInt(value) : undefined : props.document.supplierId,
                supplierName: variant === "supplierId" ? value : props.document.supplierName,
                assignedUserId: props.document.assignedUserId,
                assignedUserFullName: props.document.assignedUserFullName,
                comment: props.document.comment,
                status: props.document.status,
                type: variant === "type" ? valueType : props.document.type,
                lines: props.document.lines,
                requests: props.document.requests,
                files: props.document.files,
            }
            props.setDocument(updatedDocument);
        }
    }
    //#######################################################################
    //#######################################################################
    //#######################################################################
    const getSuppliers = async (isMounted: boolean) => {
        if (props.organizationId) {
            let businessLogic = new AccountBusinessLogic();
            setLoader(true);
            const result = await businessLogic.getAccountSuppliers(props.organizationId);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) toast(result.errors.join(", "));
            } else {
                if (isMounted) setSupplierList(result.value);
            }
        }
    }
    //#######################################################################
    //#######################################################################
    //#######################################################################
    return <Grid container direction="column" component={Paper}
        justifyContent="center"
        alignItems="center"
        sx={{
            border: 1,
            borderColor: theme.palette.primary.main,
        }}>
        <Grid xs={12} display="flex" justifyContent="center" padding={theme.spacing(2)}
            sx={{
                //borderBottom: 1,
                //borderColor: theme.palette.primary.main,
                marginBottom: theme.spacing(2),
                backgroundColor: theme.palette.grey[300],
            }}>
            <Typography variant="h6" color={theme.palette.primary.main}>{t("approver.invoiceInformation")}</Typography>
        </Grid>
        <Grid>
            <Stack direction="column" spacing={4} padding="1rem">
                <Box sx={{ minWidth: "11rem", backgroundColor: theme.palette.background.paper, }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="select-supplier">{t('basic.supplier')}</InputLabel>
                        <Select
                            labelId="select-supplier-label"
                            id="select-supplier"
                            value={props.document ? (props.document.supplierId ? props.document.supplierId.toString() : undefined) : undefined}
                            label={t('basic.supplier')}
                            disabled={props.onlyReasign}
                            onChange={(event: SelectChangeEvent) => handleDocumentUpdate("supplierId", event.target.value, undefined)}
                        >
                            <MenuItem value={undefined}>{t("")}</MenuItem>
                            {supplierList.map(e => {
                                return <MenuItem value={e?.accountId}>{e.name}</MenuItem>
                            })}
                        </Select>
                    </FormControl>
                </Box>
                <Box sx={{ minWidth: "11rem", backgroundColor: theme.palette.background.paper, }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="select-type">Type</InputLabel>
                        <Select
                            labelId="select-type-label"
                            id="select-type"
                            value={props.document ? props.document.type : undefined}
                            label="Type"
                            disabled={props.onlyReasign}
                            onChange={(event: SelectChangeEvent) => handleDocumentUpdate("type", event.target.value, undefined)}
                        >
                            <MenuItem value={"Unknown"}>{t("basic.unknown")}</MenuItem>
                            <MenuItem value={"Invoice"}>{t("basic.invoice")}</MenuItem>
                            <MenuItem value={"CreditNote"}>{t("basic.creditNote")}</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <TextField
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={props.onlyReasign}
                    value={props.document ? props.document.invoiceNo ? props.document.invoiceNo : "" : ""}
                    onChange={(event) => handleDocumentUpdate("invoiceNo", event.target.value, undefined, undefined)}
                    sx={{ minWidth: "11rem", }}
                    InputLabelProps={{ shrink: true, sx: { backgroundColor: theme.palette.background.paper, } }}
                    id="invoiceNoId" label={t("search.invoiceId")} />
                <DatePicker
                    label={t("search.voucherDate")}
                    value={props.document ? props.document.voucherDate ? props.document.voucherDate : null : null}
                    disabled={props.onlyReasign}
                    onChange={(newValue) => { handleDocumentUpdate("voucherDate", undefined, newValue) }}
                    slotProps={{ textField: { size: "small", helperText: null, InputLabelProps: { shrink: true }, sx: { minWidth: "11rem", backgrounColor: theme.palette.background.paper } } }}
                />
                <DatePicker
                    label={t("search.dueDate")}
                    value={props.document ? props.document.dueDate ? props.document.dueDate : null : null}
                    disabled={props.onlyReasign}
                    onChange={(newValue) => { handleDocumentUpdate("dueDate", undefined, newValue) }}
                    slotProps={{ textField: { size: "small", helperText: null, InputLabelProps: { shrink: true }, sx: { minWidth: "11rem", backgrounColor: theme.palette.background.paper } } }}
                />
                <TextField
                    size="small"
                    variant="outlined"
                    error={amountError()}
                    value={props.document ? props.document.amount ? props.document.amount : "" : ""}
                    disabled={props.onlyReasign}
                    color={amountError() ? "error" : "primary"}
                    onChange={(e) => {handleDocumentUpdate("amount", e.target.value, undefined)}}
                    inputProps={{ style: { textAlign: "right" } }}
                    sx={{
                        minWidth: "6.5rem",
                        backgroundColor: theme.palette.background.paper,
                    }}
                    InputLabelProps={{ shrink: true, }}
                    id="invoiceAmount" label={t("search.amount")} />
                <TextField
                    size="small"
                    variant="outlined"
                    error={cidError()}
                    color={cidError() ? "error" : "primary"}
                    value={props.document ? props.document.cid ? props.document.cid : "" : ""}
                    disabled={props.onlyReasign}
                    onChange={(e) => {handleDocumentUpdate("cid", e.target.value, undefined)}}
                    sx={{
                        minWidth: "6.5rem",
                        backgroundColor: theme.palette.background.paper,
                    }}
                    InputLabelProps={{ shrink: true, }}
                    id="invoiceCid" label={t("search.cid")} />
            </Stack>
        </Grid>
    </Grid>
}

export default DialogDocumentApproverInvoiceInformation;